import styled from '@emotion/styled';
import {
  Button,
  Input,
  Modal,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { orderBy } from 'lodash';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FaEllipsisH, FaPlus, FaRegCalendarAlt } from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';

import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { DetailTitreEntity } from '../../../../entities/detailTitre.entity';
import { TitreEntity } from '../../../../entities/titre.entity';
import { ROUTES } from '../../../../routes';
import { ORANGE, PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import { Layout } from '../../../shared/Layout';
import { StatusPill, StatusTone } from '../../shared/StatusPill';
import { exportTitresData } from '../components/exportDataExcel';
import { OperationTitreForm } from '../components/OperationTitreForm';
import { getTitres } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  > .content {
    width: 100%;
    padding: 12px;
    color: var(--mt-text);
  }

  .title-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .title-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .title-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.55;
  }

  .hero-actions,
  .page-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .primary-action {
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, ${YELLOW});
    color: #07141c;
    font-family: Poppins-B;
  }

  .ghost-action,
  .action-button {
    border-color: rgba(255, 176, 0, 0.28);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .ghost-action {
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .table-shell,
  .title-detail {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 82px;
    padding: 14px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 24px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(255, 176, 0, 0.26);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 340px) 1fr minmax(300px, 360px) auto;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .filter-tabs {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .filter-tab {
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    padding: 8px 11px;
    background: var(--mt-surface);
    color: var(--mt-muted);
    cursor: pointer;
    font-family: Poppins-B;
    font-size: 12px;
  }

  .filter-tab.active {
    border-color: rgba(255, 176, 0, 0.55);
    color: var(--mt-warning);
    background: var(--mt-row-hover);
  }

  .advanced-filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 1fr));
    gap: 8px;
  }

  .advanced-filters .ant-select-selector {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 999px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
    box-shadow: none !important;
  }

  .title-workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 390px;
    gap: 12px;
    margin-top: 12px;
  }

  .table-shell,
  .title-detail {
    overflow: hidden;
  }

  .table-title,
  .detail-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 46px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    text-transform: uppercase;
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: #fff7e8;
    font-size: 11px;
    text-transform: none;
  }

  .nested-title-table {
    margin: 0;
  }

  .nested-panel {
    margin: 8px 4px 12px;
    overflow: hidden;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background: var(--mt-card);
  }

  .nested-panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--mt-border);
    background: var(--mt-surface);
  }

  .nested-panel-head strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .nested-panel-head span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .nested-metrics {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .nested-metric {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: var(--mt-warning);
    background: rgba(255, 176, 0, 0.06);
    font-family: Poppins-B;
    font-size: 11px;
    white-space: nowrap;
  }

  .holder-cell,
  .date-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .holder-cell strong,
  .date-cell strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .holder-cell span,
  .date-cell span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .numeric-cell {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
    text-align: right;
    white-space: nowrap;
  }

  .origin-cell {
    color: var(--mt-muted);
    font-family: Poppins-B;
    font-size: 12px;
  }

  .operation-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    border-color: rgba(255, 176, 0, 0.3);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 12px;
  }

  .operation-button svg {
    color: var(--mt-warning);
  }

  .detail-body {
    display: grid;
    gap: 12px;
    padding: 14px;
  }

  .title-card {
    padding: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.08), transparent 42%),
      var(--mt-surface);
  }

  .title-card h3 {
    margin: 0;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .muted {
    color: var(--mt-muted);
    font-size: 12px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .detail-row {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-surface);
  }

  .detail-row span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    text-transform: uppercase;
  }

  .detail-row strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-text);
    font-family: Poppins-B;
    overflow-wrap: anywhere;
  }

  .detail-row.full {
    grid-column: 1 / -1;
  }

  .ant-table {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-family: Poppins-B;
    font-size: 11px;
    text-transform: uppercase;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .operation-modal .ant-modal-content {
    overflow: hidden;
    border-radius: 8px;
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .operation-modal .ant-modal-header {
    border-bottom: 1px solid var(--mt-border);
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.12), transparent 38%),
      ${PRIMARY};
  }

  .operation-modal .ant-modal-title {
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  .operation-modal .ant-modal-close {
    color: #fff7e8;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
  }

  .even-row {
    background-color: var(--mt-row-even);
  }

  .odd-row:hover > td,
  .even-row:hover > td,
  .selected-row > td {
    background-color: var(--mt-row-hover) !important;
    cursor: pointer;
  }

  @media (max-width: 760px) {
    > .content {
      padding: 10px;
    }

    .title-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid,
    .title-workspace,
    .advanced-filters,
    .detail-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className='detail-row'>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const MATURITY_FILTERS = [
  { label: 'Toutes échéances', value: 'all' },
  { label: '0-12 mois', value: 'short' },
  { label: '1-5 ans', value: 'medium' },
  { label: '5 ans+', value: 'long' },
];

export const EmissionTitrePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [titres, setTitres] = useState<TitreEntity[]>([]);
  const [filterTitres, setFilterTitres] = useState<TitreEntity[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [instrumentFilter, setInstrumentFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [maturityFilter, setMaturityFilter] = useState('all');
  const [activeExpRow, setActiveExpRow] = useState<string[]>([]);
  const [selectedTitre, setSelectedTitre] = useState<TitreEntity>();
  const [selectedDetailTitre, setSelectedDetailTitre] =
    useState<DetailTitreEntity>();
  const [showModal, setShowModal] = useState(false);
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;
  const history = useHistory();

  const fetchData = () => {
    setIsLoading(true);
    getTitres(connectedUser.id).then(({ data }) => {
      const orderedTitres = orderBy(data, 'created_at', 'desc');
      setTitres(orderedTitres);
      setFilterTitres(orderedTitres);
      setSelectedTitre(orderedTitres?.[0]);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const term = searchTerm.toLowerCase().trim();
    setFilterTitres(
      titres.filter((titre) => {
        const searchable = [
          titre.code,
          titre.pays,
          titre.instrument,
          titre.dateEmission,
          titre.dateMaturite,
          titre.created_at,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const matchesSearch = !term || searchable.includes(term);
        const matchesInstrument =
          instrumentFilter === 'all' || titre.instrument === instrumentFilter;
        const matchesCountry = countryFilter === 'all' || titre.pays === countryFilter;
        const matchesMaturity =
          maturityFilter === 'all' || getMaturityBucket(titre) === maturityFilter;

        return (
          matchesSearch &&
          matchesInstrument &&
          matchesCountry &&
          matchesMaturity
        );
      }),
    );
  }, [countryFilter, instrumentFilter, maturityFilter, searchTerm, titres]);

  const titleStats = useMemo(() => {
    const totalDetails = titres.flatMap((titre) => titre.details || []);
    const totalQuantity = totalDetails.reduce(
      (sum, detail) => sum + Number(detail.qte || 0),
      0,
    );
    const totalAmount = totalDetails.reduce(
      (sum, detail) => sum + Number(detail.montant || 0),
      0,
    );
    const issuers = new Set(titres.map((titre) => titre.pays).filter(Boolean));
    const upcoming = titres.filter((titre) =>
      moment(titre.dateMaturite).isBefore(moment().add(12, 'months')),
    ).length;

    return {
      total: titres.length,
      issuers: issuers.size,
      totalQuantity,
      totalAmount,
      upcoming,
    };
  }, [titres]);

  const instrumentOptions = useMemo(
    () =>
      getUniqueOptions(
        titres
          .map((titre) => ({ label: titre.instrument, value: titre.instrument }))
          .filter((option) => option.value),
      ),
    [titres],
  );

  const countryOptions = useMemo(
    () =>
      getUniqueOptions(
        titres
          .map((titre) => ({ label: titre.pays, value: titre.pays }))
          .filter((option) => option.value),
      ),
    [titres],
  );

  const resetFilters = () => {
    setSearchTerm('');
    setInstrumentFilter('all');
    setCountryFilter('all');
    setMaturityFilter('all');
  };

  const EXPANDED_COLUMNS: TableColumnsType<DetailTitreEntity> = [
    {
      title: 'Détenteur',
      width: 260,
      render: (_cell: any, row) => (
        <div className='holder-cell'>
          <strong>{getDetailHolderName(row)}</strong>
          <span>
            Réf. {row.detenteur?.code_referent || '--'} · Sous-code {row.subCode || '--'}
          </span>
        </div>
      ),
    },
    {
      title: 'Quantité',
      dataIndex: 'qte',
      align: 'right' as const,
      render: (cell: any) => (
        <span className='numeric-cell'>{formatNum(Number(cell)) || '--'}</span>
      ),
    },
    {
      title: 'Montant',
      dataIndex: 'montant',
      align: 'right' as const,
      render: (cell: any) => (
        <span className='numeric-cell'>{formatNum(Number(cell)) || '--'} XAF</span>
      ),
    },
    {
      title: 'Prix nominal',
      dataIndex: 'prixNominale',
      align: 'right' as const,
      render: (cell: any) => (
        <span className='numeric-cell'>{formatNum(Number(cell)) || '--'}</span>
      ),
    },
    {
      title: 'Décote (%)',
      dataIndex: 'decote',
      align: 'right' as const,
      render: (cell: any) => (
        <span className='numeric-cell'>{formatNum(Number(cell)) || '--'}%</span>
      ),
    },
    {
      title: 'Rendement (%)',
      dataIndex: 'tauxRendement',
      align: 'right' as const,
      render: (cell: any) => (
        <span className='numeric-cell'>{formatNum(Number(cell)) || '--'}%</span>
      ),
    },
    {
      title: 'Dates',
      width: 160,
      render: (_cell: any, row) => (
        <div className='date-cell'>
          <strong>{formatDate(row.dateAcquisition)}</strong>
          <span>Attribué {formatDate(row.created_at)}</span>
        </div>
      ),
    },
    {
      title: 'Origine',
      dataIndex: 'originCode',
      render: (cell: string) => <span className='origin-cell'>{cell || '--'}</span>,
    },
    {
      title: 'Etat',
      dataIndex: 'etat',
      render: (cell: string) => (
        <StatusPill tone={getEtatTone(cell)}>{cell || '--'}</StatusPill>
      ),
    },
    {
      title: 'Actions',
      width: 120,
      render: (_cell: any, row) => (
        <Button
          icon={<FaEllipsisH />}
          className='operation-button'
          onClick={(event) => {
            event.stopPropagation();
            setSelectedDetailTitre(row);
            setShowModal(true);
          }}
        >
          Opérer
        </Button>
      ),
      align: 'center' as const,
    },
  ];

  return (
    <Layout>
      <Container>
        <div className='content'>
          <section className='title-hero'>
            <div>
              <span className='eyebrow'>Portefeuille des titres</span>
              <h1>Gestion des titres</h1>
              <p>
                Supervisez les instruments, leurs détenteurs, quantités,
                maturités et opérations post-marché depuis un registre unique.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='ghost-action'
                onExport={(format) => exportTitresData(filterTitres, format, getExportMetadata(connectedUser))}
              />
              <Button
                icon={<FaPlus style={{ marginBottom: -2, marginRight: 5 }} />}
                className='primary-action'
                onClick={() => history.push(ROUTES.PANEL.EMISSION.NEW_TITRE)}
              >
                Nouveau titre
              </Button>
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard label='Titres total' value={titleStats.total} icon={<FaRegCalendarAlt />} />
            <KpiCard label='Émetteurs' value={titleStats.issuers} icon={<FaRegCalendarAlt />} />
            <KpiCard
              label='Quantité totale'
              value={formatNum(titleStats.totalQuantity) || '--'}
              icon={<FaRegCalendarAlt />}
            />
            <KpiCard
              label='Montant total'
              value={`${formatNum(titleStats.totalAmount) || '--'} XAF`}
              icon={<FaRegCalendarAlt />}
            />
            <KpiCard label='Échéances 12 mois' value={titleStats.upcoming} icon={<FaRegCalendarAlt />} />
          </section>

          <section className='toolbar'>
            <div className='search-box'>
              <FiSearch />
              <Input
                placeholder='Rechercher code, émetteur, type...'
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <div className='filter-tabs'>
              {MATURITY_FILTERS.map((filter) => (
                <button
                  className={`filter-tab ${
                    maturityFilter === filter.value ? 'active' : ''
                  }`}
                  key={filter.value}
                  type='button'
                  onClick={() => setMaturityFilter(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className='advanced-filters'>
              <Select
                value={instrumentFilter}
                onChange={setInstrumentFilter}
                options={[
                  { label: 'Tous instruments', value: 'all' },
                  ...instrumentOptions,
                ]}
              />
              <Select
                value={countryFilter}
                onChange={setCountryFilter}
                options={[
                  { label: 'Tous émetteurs', value: 'all' },
                  ...countryOptions,
                ]}
              />
            </div>
            <div className='page-toolbar'>
              <Button className='action-button' onClick={resetFilters}>
                Réinitialiser
              </Button>
                <PulseLoader
                  size={5}
                  margin={2}
                  color={YELLOW}
                  loading={isLoading}
                />
            </div>
          </section>

          <section className='title-workspace'>
            <div className='table-shell'>
              <div className='table-title'>
                <span>Registre des titres</span>
                <span className='table-count'>{filterTitres.length} titres</span>
              </div>
            <Table
              columns={COLUMNS}
              dataSource={filterTitres}
              rowClassName={(row, index) =>
                `${index % 2 === 0 ? 'even-row' : 'odd-row'} ${
                  selectedTitre?.id === row.id ? 'selected-row' : ''
                }`
              }
              size='small'
              loading={isLoading}
              scroll={{ x: 1080 }}
              rowKey='id'
              pagination={{ pageSize: 12 }}
              onRow={(row) => ({
                onClick: () => setSelectedTitre(row),
              })}
              expandable={{
                expandedRowRender: (row) => {
                  return (
                    <div className='nested-panel'>
                      <div className='nested-panel-head'>
                        <div>
                          <strong>Détenteurs du titre {row.code}</strong>
                          <span>{row.details?.length || 0} positions rattachées</span>
                        </div>
                        <div className='nested-metrics'>
                          <span className='nested-metric'>
                            Qté {formatNum(getTitreQuantity(row)) || '--'}
                          </span>
                          <span className='nested-metric'>
                            Montant {formatNum(getTitreAmount(row)) || '--'} XAF
                          </span>
                        </div>
                      </div>
                      <Table<DetailTitreEntity>
                        className='nested-title-table'
                        columns={EXPANDED_COLUMNS}
                        dataSource={row.details || []}
                        rowKey='id'
                        size='small'
                        scroll={{ x: 1040 }}
                        pagination={false}
                      />
                    </div>
                  );
                },
                expandedRowKeys: activeExpRow,
                onExpand: (expanded, row) => {
                  const keys: string[] = [];
                  if (expanded) {
                    keys.push(row.id);
                  }
                  setActiveExpRow(keys);
                },
              }}
            />
            </div>

            <aside className='title-detail'>
              <div className='detail-title'>Fiche titre</div>
              {selectedTitre ? (
                <div className='detail-body'>
                  <div className='title-card'>
                    <h3>{selectedTitre.code}</h3>
                    <span className='muted'>
                      {selectedTitre.pays} · {selectedTitre.instrument}
                    </span>
                  </div>
                  <div className='detail-grid'>
                    <DetailRow label='Marché' value={selectedTitre.marche || '--'} />
                    <DetailRow label='Région' value={selectedTitre.region || '--'} />
                    <DetailRow label='Coupon' value={`${formatNum(Number(selectedTitre.tauxMarginal))}%`} />
                    <DetailRow label='Durée' value={`${selectedTitre.dureeMaturite || '--'} mois`} />
                    <DetailRow label='Fréquence' value={selectedTitre.frequencePaiement || '--'} />
                    <DetailRow label='Valeur nominale' value={formatNum(Number(selectedTitre.valeurNominale || 0)) || '--'} />
                    <DetailRow label="Date d'émission" value={formatDate(selectedTitre.dateEmission)} />
                    <DetailRow label='Date maturité' value={formatDate(selectedTitre.dateMaturite)} />
                    <DetailRow
                      label='Détenteurs'
                      value={`${selectedTitre.details?.length || 0}`}
                    />
                    <DetailRow
                      label='Quantité totale'
                      value={formatNum(getTitreQuantity(selectedTitre)) || '--'}
                    />
                  </div>
                  <Button
                    className='primary-action'
                    onClick={() => history.push(ROUTES.PANEL.EMISSION.NEW_TITRE)}
                  >
                    Émettre un nouveau titre
                  </Button>
                </div>
              ) : (
                <div className='detail-body muted'>
                  Sélectionnez un titre pour consulter sa fiche.
                </div>
              )}
            </aside>
          </section>
        </div>
        <Modal
          getContainer={false}
          open={showModal}
          className='operation-modal'
          title={
            <p style={{ textAlign: 'center', margin: 0 }}>
              Effectuer des opérations sur le titre
            </p>
          }
          onCancel={() => setShowModal(false)}
          footer={null}
          width='min(100vw, 720px)'
        >
          <OperationTitreForm
            setShowModal={setShowModal}
            detailTitre={selectedDetailTitre}
          />
        </Modal>
      </Container>
    </Layout>
  );
};

const COLUMNS = [
  {
    title: 'CODE',
    dataIndex: 'code',
  },
  {
    title: 'Emetteur',
    dataIndex: 'pays',
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
  },
  {
    title: "Date d'emission",
    dataIndex: 'dateEmission',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
  {
    title: 'Durée (mois)',
    dataIndex: 'dureeMaturite',
  },
  {
    title: 'Coupon (%)',
    dataIndex: 'tauxMarginal',
    render: (cell: any) => `${formatNum(Number(cell))}`,
  },
  {
    title: 'Fréquence',
    dataIndex: 'frequencePaiement',
  },
  {
    title: "Date d'ajout",
    dataIndex: 'created_at',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
  },
];

const getTitreQuantity = (titre: TitreEntity) =>
  (titre.details || []).reduce((sum, detail) => sum + Number(detail.qte || 0), 0);

const getTitreAmount = (titre: TitreEntity) =>
  (titre.details || []).reduce(
    (sum, detail) => sum + Number(detail.montant || 0),
    0,
  );

const getDetailHolderName = (detail: DetailTitreEntity) => {
  const holder = detail.detenteur;
  const name = [holder?.nom, holder?.prenom].filter(Boolean).join(' ');

  return name || '--';
};

const getMaturityBucket = (titre: TitreEntity) => {
  const months = moment(titre.dateMaturite).diff(moment(), 'months');

  if (months <= 12) return 'short';
  if (months <= 60) return 'medium';
  return 'long';
};

const getEtatTone = (etat?: string): StatusTone => {
  const normalized = etat?.toLowerCase() || '';

  if (normalized.includes('liv') || normalized.includes('actif')) {
    return 'success';
  }

  if (normalized.includes('attente') || normalized.includes('cours')) {
    return 'warning';
  }

  if (normalized.includes('rejet') || normalized.includes('défaut')) {
    return 'danger';
  }

  return 'neutral';
};

const getUniqueOptions = (options: { label: string; value: string }[]) =>
  Array.from(new Map(options.map((option) => [option.value, option])).values());

const formatDate = (date?: string) =>
  date && moment(date).isValid() ? moment(date).format('DD/MM/YYYY') : '--';
